import React, { useEffect, useMemo, useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { DailySeries } from '../../services/api';

interface RevenueTrendChartProps {
  current: DailySeries[];
  comparison: DailySeries[];
  periodLabel: string;
}

type Granularity = 'day' | 'week' | 'month';

const formatCurrency = (value: number) => {
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const autoGranularity = (days: number): Granularity =>
  days > 120 ? 'month' : days > 35 ? 'week' : 'day';

interface Bucket {
  label: string;
  current: number;
  comparison: number;
}

/**
 * Aggregate the aligned daily series into buckets. Comparison stays aligned by
 * index (day 1 vs day 1 of last year), so bucketing by index keeps both series
 * comparable. Month buckets follow the current period's calendar months.
 */
const bucketize = (
  current: DailySeries[],
  comparison: DailySeries[],
  granularity: Granularity,
  metric: 'netRevenue' | 'txCount',
): Bucket[] => {
  if (granularity === 'day') {
    return current.map((d, i) => ({
      label: format(parseISO(d.day), 'MMM d'),
      current: d[metric],
      comparison: comparison[i] ? comparison[i][metric] : 0,
    }));
  }

  const keyOf = (i: number) =>
    granularity === 'week'
      ? Math.floor(i / 7)
      : format(parseISO(current[i].day), 'yyyy-MM');

  const buckets: Bucket[] = [];
  let currentKey: string | number | null = null;
  for (let i = 0; i < current.length; i++) {
    const key = keyOf(i);
    if (key !== currentKey) {
      currentKey = key;
      buckets.push({
        label: format(parseISO(current[i].day), granularity === 'week' ? 'MMM d' : 'MMM'),
        current: 0,
        comparison: 0,
      });
    }
    const b = buckets[buckets.length - 1];
    b.current += current[i][metric];
    b.comparison += comparison[i] ? comparison[i][metric] : 0;
  }
  return buckets;
};

const accumulate = (buckets: Bucket[]): Bucket[] => {
  let c = 0;
  let p = 0;
  return buckets.map(b => {
    c += b.current;
    p += b.comparison;
    return { ...b, current: c, comparison: p };
  });
};

const GRANULARITY_OPTIONS: Array<{ key: Granularity; label: string; minDays: number }> = [
  { key: 'day', label: 'Daily', minDays: 0 },
  { key: 'week', label: 'Weekly', minDays: 8 },
  { key: 'month', label: 'Monthly', minDays: 32 },
];

const TrendTooltip: React.FC<any> = ({ active, payload, label, isCurrency, currentName }) => {
  if (!active || !payload || payload.length === 0) return null;
  const row = payload[0]?.payload as Bucket | undefined;
  if (!row) return null;
  const fmt = (v: number) =>
    isCurrency ? `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : v.toLocaleString();
  const deltaPct = row.comparison !== 0
    ? ((row.current - row.comparison) / Math.abs(row.comparison)) * 100
    : null;
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-md px-3 py-2 text-xs">
      <div className="font-semibold text-gray-900 mb-1">{label}</div>
      <div className="flex items-center justify-between gap-6">
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <span className="w-2 h-2 rounded-full bg-blue-600" /> {currentName}
        </span>
        <span className="font-semibold text-gray-900 tabular-nums">{fmt(row.current)}</span>
      </div>
      <div className="flex items-center justify-between gap-6">
        <span className="inline-flex items-center gap-1.5 text-gray-500">
          <span className="w-2 h-2 rounded-full bg-gray-300" /> Last Year
        </span>
        <span className="text-gray-600 tabular-nums">{fmt(row.comparison)}</span>
      </div>
      {deltaPct !== null && (
        <div className={`mt-1 pt-1 border-t border-gray-100 text-right font-semibold ${
          deltaPct >= 0 ? 'text-emerald-600' : 'text-red-600'
        }`}>
          {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}% YoY
        </div>
      )}
    </div>
  );
};

export const RevenueTrendChart: React.FC<RevenueTrendChartProps> = ({ current, comparison, periodLabel }) => {
  const [metric, setMetric] = useState<'netRevenue' | 'txCount'>('netRevenue');
  const [granularityOverride, setGranularityOverride] = useState<Granularity | null>(null);
  const [cumulative, setCumulative] = useState(false);

  const days = current.length;
  const granularity = granularityOverride ?? autoGranularity(days);

  // A new period invalidates a manual granularity choice (e.g. Monthly on "Today")
  useEffect(() => { setGranularityOverride(null); }, [periodLabel, days]);

  const chartData = useMemo(() => {
    const buckets = bucketize(current, comparison, granularity, metric);
    return cumulative ? accumulate(buckets) : buckets;
  }, [current, comparison, granularity, metric, cumulative]);

  const currentName = periodLabel === 'ytd' ? 'This Year' : 'Current';
  const isCurrency = metric === 'netRevenue';
  const availableGranularities = GRANULARITY_OPTIONS.filter(g => days >= g.minDays);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-4">
          <h3 className="text-base font-semibold text-gray-900">Revenue Trend</h3>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-blue-600" /> {currentName}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-gray-300" /> Last Year
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {availableGranularities.length > 1 && (
            <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
              {availableGranularities.map(g => (
                <button
                  key={g.key}
                  onClick={() => setGranularityOverride(g.key)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                    granularity === g.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setCumulative(v => !v)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
              cumulative
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'bg-white border-gray-200 text-gray-500 hover:text-gray-700'
            }`}
          >
            Cumulative
          </button>
          <div className="flex gap-0.5 bg-gray-100 rounded-lg p-0.5">
            {([['netRevenue', 'Revenue'], ['txCount', 'Transactions']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setMetric(key)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  metric === key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
            <defs>
              <linearGradient id="trendCurrent" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
              </linearGradient>
              <linearGradient id="trendComparison" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#9ca3af" stopOpacity={0.15} />
                <stop offset="100%" stopColor="#9ca3af" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              minTickGap={24}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              width={52}
              tickFormatter={isCurrency ? formatCurrency : undefined}
            />
            <Tooltip content={<TrendTooltip isCurrency={isCurrency} currentName={currentName} />} />
            <Area
              type="monotone"
              dataKey="comparison"
              name="Last Year"
              stroke="#9ca3af"
              fill="url(#trendComparison)"
              strokeWidth={1.5}
              strokeDasharray="5 5"
            />
            <Area
              type="monotone"
              dataKey="current"
              name={currentName}
              stroke="#2563eb"
              fill="url(#trendCurrent)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
      {granularity !== 'day' && !cumulative && (
        <p className="text-[11px] text-gray-400 mt-2">
          {granularity === 'week' ? 'Weekly totals; the last point may be a partial week.' : 'Monthly totals; the last point may be a partial month.'}
        </p>
      )}
    </div>
  );
};
